import React, { useState, useEffect, useMemo } from "react";
import { styled } from "@glitz/react";
import { useSearchParams } from "react-router-dom";
import { Product } from "../types";
import { spacing, fontSize } from "../theme";
import ProductCard from "../components/ProductCard";
import Select from "../components/basic/Select";

const API_BASE = process.env.REACT_APP_API_URL || "";

const sortOptions = [
  { value: "name", label: "Namn (A–Ö)" },
  { value: "priceLow", label: "Lägsta pris" },
  { value: "priceHigh", label: "Högsta pris" },
];

const SearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("name");

  useEffect(() => {
    if (!query.trim()) {
      setProducts([]);
      return;
    }
    setLoading(true);
    fetch(`${API_BASE}/api/products/search?q=${encodeURIComponent(query)}`)
      .then((res) => res.json())
      .then((data) => setProducts(Array.isArray(data) ? data : []))
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [query]);

  const sortedProducts = useMemo(() => {
    const sorted = [...products];
    switch (sort) {
      case "priceLow":
        return sorted.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
      case "priceHigh":
        return sorted.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
      case "name":
      default:
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [sort, products]);

  return (
    <PageWrapper>
      <TopWrapper>
        <Header>
          {query ? `Sökresultat för "${query}"` : "Sök"}
        </Header>
        {products.length > 0 && (
          <SortSelect
            onChange={(val) => setSort(val)}
            options={sortOptions}
            label="Sortera"
            value={sort}
          />
        )}
      </TopWrapper>

      {loading && <StatusText>Söker…</StatusText>}

      {!loading && query && products.length === 0 && (
        <StatusText>Inga produkter hittades för "{query}".</StatusText>
      )}

      {!loading && products.length > 0 && (
        <ResultInfo>{sortedProducts.length} produkter</ResultInfo>
      )}

      <ProductGrid className="search-product-grid">
        {sortedProducts.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </ProductGrid>
    </PageWrapper>
  );
};

const PageWrapper = styled.div({
  paddingLeft: spacing.m,
  paddingRight: spacing.m,
  minHeight: "60vh",
});

const TopWrapper = styled.div({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const Header = styled.h1({
  fontSize: fontSize.xl,
});

const SortSelect = styled(Select, {
  width: "300px",
  marginRight: spacing.m,
});

const StatusText = styled.p({
  fontSize: fontSize.m,
  color: "#666",
});

const ResultInfo = styled.p({
  fontSize: fontSize.s,
  color: "#999",
  margin: { top: 0, bottom: spacing.m },
});

const ProductGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: spacing.m,
  marginBottom: spacing.m,
});

export default SearchPage;
